import type { LocationResponseDto, OperatingHoursDto, TimeRangeDto } from '@/types/brand';

/**
 * What the backend evaluates a schedule in when the location never set a zone — the column default
 * on `locations.timezone`. Keep the two in sync.
 */
const DEFAULT_TIMEZONE = 'America/Montevideo';

/** Sunday-first, so `Date#getDay()`-style indices line up. */
const DAY_KEYS: ReadonlyArray<keyof OperatingHoursDto> = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const MINUTES_PER_DAY = 24 * 60;

function toMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

/**
 * Whether a range runs to (or past) midnight. As a close, `00:00` and `23:59` both mean "until the
 * end of the day" (the location editor says so in as many words); a close earlier than its open
 * means the range itself crosses midnight.
 */
function runsToMidnight(range: TimeRangeDto): boolean {
  const open = toMinutes(range.open);
  if (open === null) return false;
  const close = range.close === '00:00' || range.close === '23:59' ? MINUTES_PER_DAY : toMinutes(range.close);
  return close !== null && (close >= MINUTES_PER_DAY || close <= open);
}

/**
 * When this day's service starts, in minutes past its own midnight. Ranges never cross midnight, so
 * a schedule like 19:00–02:00 is stored as 19:00–23:59 on one day and 00:00–02:00 on the next — and
 * that `00:00` range is the TAIL of yesterday's session, not the start of today's. It is skipped
 * whenever yesterday runs to midnight and the range itself closes early; a 00:00 range that runs to
 * the end of the day is a genuine opening (a 24 h store) and its day starts at midnight.
 */
function dayStartMinutes(hours: OperatingHoursDto, dayIndex: number): number | null {
  const yesterday = hours[DAY_KEYS[(dayIndex + DAY_KEYS.length - 1) % DAY_KEYS.length]] ?? [];
  const overnightTail = yesterday.some(runsToMidnight);
  let earliest: number | null = null;
  for (const range of hours[DAY_KEYS[dayIndex]] ?? []) {
    const open = toMinutes(range.open);
    if (open === null) continue;
    if (open === 0 && overnightTail && !runsToMidnight(range)) continue;
    if (earliest === null || open < earliest) earliest = open;
  }
  return earliest;
}

/**
 * How many minutes ago the CURRENT business day of one location began, evaluated in its own zone:
 * today's opening once it has passed, otherwise yesterday's — which is where an order placed at
 * 00:30 during an overnight service still belongs until the kitchen opens again. Null when the
 * location has no usable schedule.
 */
function minutesSinceBusinessDayStart(
  hours: OperatingHoursDto | undefined,
  timezone: string | undefined,
  now: Date,
): number | null {
  if (!hours) return null;

  const tz = timezone || DEFAULT_TIMEZONE;
  let weekday: string;
  let time: string;
  try {
    weekday = now.toLocaleDateString('en-US', { weekday: 'long', timeZone: tz }).toLowerCase();
    time = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: tz });
  } catch {
    return null;
  }

  // `hour12: false` yields "24:05" for the first hour of the day in some engines.
  const nowMinutes = toMinutes(time.replace(/^24:/, '00:'));
  const todayIndex = DAY_KEYS.indexOf(weekday as keyof OperatingHoursDto);
  if (nowMinutes === null || todayIndex === -1) return null;

  const todayStart = dayStartMinutes(hours, todayIndex);
  if (todayStart !== null && todayStart <= nowMinutes) return nowMinutes - todayStart;

  const yesterdayIndex = (todayIndex + DAY_KEYS.length - 1) % DAY_KEYS.length;
  const yesterdayStart = dayStartMinutes(hours, yesterdayIndex);
  if (yesterdayStart !== null) return nowMinutes + MINUTES_PER_DAY - yesterdayStart;

  return null;
}

/** Midnight at the start of today on the operator's own clock — the bound when no schedule helps. */
export function localMidnight(now: Date): Date {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * When "today" began for the board: the most recent opening of the branches in scope, so that a
 * service which ran past midnight keeps its orders on the board until the next opening — and not one
 * minute longer. Several branches (or "every branch") take the EARLIEST of their openings, so no
 * branch's orders disappear while its own day is still running. Without a single usable schedule
 * the day starts at the operator's local midnight.
 *
 * The result is floored to the minute, so a caller ticking every few seconds gets the same instant
 * back until the boundary actually moves.
 */
export function businessDayStart(locations: readonly LocationResponseDto[], now: Date): Date {
  const nowMinute = new Date(Math.floor(now.getTime() / 60_000) * 60_000);
  let longest: number | null = null;
  for (const location of locations) {
    const minutes = minutesSinceBusinessDayStart(location.operatingHours, location.timezone, nowMinute);
    if (minutes === null) continue;
    if (longest === null || minutes > longest) longest = minutes;
  }
  if (longest === null) return localMidnight(now);
  return new Date(nowMinute.getTime() - longest * 60_000);
}
